/*
  TASK-5051 — one-tap screenshot of the order-check result on
  /pages/delayed-delivery.

  WHAT IT DOES
  Tapping the localized order-check heading (`custom.delayed.check_title`, the
  <h2> inside #ils-ds-check) saves a PNG of the EXACT rendered DOM range
  #ils-ds-check — i.e. from that heading, through the order-number and email
  fields with their hints, the check button, the localized result heading, every
  delayed product card, down to and including the final summary block. Nothing
  is re-generated or re-designed: the image is the page's own pixels, so it is
  correct in every locale, currency and viewport by construction.

  WHY A SEPARATE FILE
  assets/custom-delayed-shipping.js owns the lookup, the neutral-miss contract
  and the server-side lock mirror. None of that is touched here: this file only
  OBSERVES [data-ds-results] and reads the DOM. A bug in the screenshot feature
  therefore cannot change what the checker does or says.

  WHY #ils-ds-check IS THE EXACT RANGE
  #ils-ds-check is the .ils-ds__block whose first child is the heading and whose
  last child is .ils-ds__card; the card's last child is [data-ds-results], whose
  last child on success is .ils-ds__summary. So the element's own bounding box
  already starts at the first heading pixel and ends at the card edge just below
  the summary — the storefront header, the page intro above it and the FAQ below
  it are all outside the box. The sticky header cannot bleed in either, because
  html2canvas rasterises the element's SUBTREE, it does not photograph the
  viewport (contrast Playwright's element.screenshot, which composites fixed
  chrome — TASK-5000).

  PRIVACY
  Everything happens in this browser: no upload, no third-party screenshot
  service, no server-side storage. The only network traffic is (a) the local
  theme asset custom-html2canvas.min.js, fetched on first use so the page keeps
  its current weight for everyone who never taps the heading, and (b) re-reads
  of the product images that are ALREADY on this page, from the store's own
  Shopify CDN.

  IMAGES
  The product thumbnails are fetched to data URLs BEFORE the capture and swapped
  in on the clone. That solves three problems in one step: the canvas is never
  tainted (so toBlob cannot throw a SecurityError), `loading="lazy"` images that
  were never scrolled into view are still present, and `object-fit: cover` —
  which html2canvas 1.4.1 does not implement — is applied here by cropping to
  the box's own aspect ratio, so a non-square product photo can never come out
  stretched.

  SAVING (TASK-5055)
  On a MOBILE browser the PNG goes to navigator.share({ files: [file] }) as an
  image/png File, because the OS share sheet is the only standards-based route
  that treats the result as an IMAGE rather than as a download: iOS offers "Save
  Image" into Photos, Android offers Google Photos / Files / Gallery. No web API
  can write into the OS screenshot album itself without a native app, none is
  faked here, and no app install is required. Android used to get a plain file
  download — that is the behaviour this task replaced.

  The share sheet needs transient user activation, which Chrome expires 5s after
  the tap. End to end the capture measured 1.25s on a Pixel-7-class mobile
  profile against live PROD with the longest result the page can produce (5
  cards, 354x1715 CSS px), so the call normally still holds activation; when it
  does not (slow device, very long result) share() rejects with NotAllowedError
  and the blob is downloaded instead. A user-CANCELLED share sheet already did
  what the user wanted and must NOT download a second copy, so exactly one thing
  happens per tap either way. Desktop keeps the direct download on purpose:
  Windows and macOS Chrome would otherwise raise an OS share sheet, which is
  worse than a file. data-shot-route on the section records the path that ran.

  GUTTER (TASK-5055)
  html2canvas crops exactly to #ils-ds-check's border box, so the card's 1px
  border landed on the very first and last column of pixels and the result read
  as a tight crop instead of a screenshot. The finished canvas is therefore
  re-drawn once onto a canvas that is larger on all four sides, filled with the
  page's own background colour, inset by the page's OWN gutter — the computed
  horizontal padding of .ils-ds__inner, i.e. 18px on mobile and 24px from 750px
  up (custom-delayed-shipping.css), not an invented border. It is a pure
  post-process on the bitmap: the page and the clone are untouched, so no reflow
  and no re-wrapped text can sneak in. Padding the ELEMENT instead would narrow
  its content box and re-wrap every line, which is exactly what must not happen.
  Nothing is lost to the crop either: measured on the live page, no descendant of
  #ils-ds-check paints outside its border box and nothing inside the range
  carries a box-shadow, so the border box really is the whole picture.

  THE DAWN-CLONE TRAP
  html2canvas clones the WHOLE document, and its createElementClone() uses a
  SHALLOW cloneNode(false). For a custom element that means the constructor runs
  immediately on an element with no children, so Dawn's <cart-drawer> and
  <predictive-search> throw ("reading 'addEventListener'", "reading 'form'") —
  three uncaught errors per screenshot, on a correct image. ignoreElements keeps
  those two subtrees out of the clone, which measured PIXEL-IDENTICAL (mean
  absolute difference 0, worst channel 0 over 708x2434) to the unfiltered clone.

  Skipping anything is still a risk, so the list is narrow AND checked at run
  time: cloneIsFaithful() re-measures the range inside the clone and the capture
  is redone with no skipping at all if its box changed. The blunt version of the
  idea — skip everything outside the target — fails that check loudly: it
  produced 748x2332 where the page is 708x2434. See cloneIsFaithful() for why the
  check is on SIZE and not on position.

  GUARD (requirement 6)
  The heading is inert until [data-ds-results] actually holds at least one
  .ils-ds__item. A miss, the neutral error, the "no delayed items" box and the
  rate-limit box all leave it inert, so a tap can never save a misleading
  almost-empty image. Failures are silent by design: this page's copy is a
  translated key set and a hint string would mean a new key in 9 market/locale
  pairs per theme for an error the visitor cannot act on.
*/
(function () {
  'use strict';

  var LIB_ATTR = 'data-shot-lib';
  var MAX_SIDE = 8192;      // iOS Safari refuses larger canvas dimensions
  var MAX_AREA = 16000000;  // and caps total canvas area just above this
  var libPromise = null;

  // Custom elements whose constructor cannot survive html2canvas' shallow clone.
  // Both live OUTSIDE the capture range (cart drawer, header search), so their
  // pixels were never going to be in the image either way.
  var SKIP_TAGS = { 'CART-DRAWER': 1, 'CART-DRAWER-ITEMS': 1, 'PREDICTIVE-SEARCH': 1 };

  function loadLib(src) {
    if (window.html2canvas) return Promise.resolve(window.html2canvas);
    if (libPromise) return libPromise;
    if (!src) return Promise.reject(new Error('no lib src'));
    libPromise = new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = src;
      s.async = true;
      s.onload = function () {
        if (window.html2canvas) resolve(window.html2canvas);
        else reject(new Error('lib did not register'));
      };
      s.onerror = function () { libPromise = null; reject(new Error('lib failed to load')); };
      document.head.appendChild(s);
    });
    return libPromise;
  }

  // Re-draw one <img> at its own rendered box size, applying the object-fit the
  // CSS asks for, and hand back a data URL. Rejects rather than guessing, so the
  // caller can fall back to the live src + useCORS.
  function inlineImage(img, scale) {
    var rect = img.getBoundingClientRect();
    var boxW = Math.max(1, Math.round(rect.width));
    var boxH = Math.max(1, Math.round(rect.height));
    var fit = (window.getComputedStyle(img).objectFit || 'fill').trim();
    var src = img.currentSrc || img.src;
    if (!src || src.indexOf('data:') === 0) return Promise.reject(new Error('skip'));

    return fetch(src, { mode: 'cors', credentials: 'omit' })
      .then(function (r) { if (!r.ok) throw new Error('img ' + r.status); return r.blob(); })
      .then(function (blob) {
        return new Promise(function (resolve, reject) {
          var url = URL.createObjectURL(blob);
          var im = new Image();
          im.onload = function () { URL.revokeObjectURL(url); resolve(im); };
          im.onerror = function () { URL.revokeObjectURL(url); reject(new Error('decode')); };
          im.src = url;
        });
      })
      .then(function (im) {
        var cv = document.createElement('canvas');
        cv.width = Math.round(boxW * scale);
        cv.height = Math.round(boxH * scale);
        var cx = cv.getContext('2d');
        var nw = im.naturalWidth || boxW;
        var nh = im.naturalHeight || boxH;
        var boxRatio = boxW / boxH;
        var imgRatio = nw / nh;
        if (fit === 'cover') {
          var sw = nw, sh = nh, sx = 0, sy = 0;
          if (imgRatio > boxRatio) { sw = nh * boxRatio; sx = (nw - sw) / 2; }
          else { sh = nw / boxRatio; sy = (nh - sh) / 2; }
          cx.drawImage(im, sx, sy, sw, sh, 0, 0, cv.width, cv.height);
        } else if (fit === 'contain') {
          // `contain` letterboxes instead of cropping: the whole image is kept
          // and the box is padded, which is what the browser paints.
          var dw = Math.min(cv.width, cv.height * imgRatio);
          var dh = Math.min(cv.height, cv.width / imgRatio);
          cx.drawImage(im, 0, 0, nw, nh, (cv.width - dw) / 2, (cv.height - dh) / 2, dw, dh);
        } else {
          cx.drawImage(im, 0, 0, cv.width, cv.height);
        }
        return cv.toDataURL('image/png');
      });
  }

  function isIOS() {
    var ua = navigator.userAgent || '';
    return /iPad|iPhone|iPod/.test(ua) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  }

  // "Is this a phone or a tablet, where the share sheet is the right save UX?"
  // The UA test comes BEFORE navigator.userAgentData.mobile on purpose: an
  // Android TABLET reports mobile:false, and its share sheet is still the only
  // route into the gallery. The coarse-pointer test is the last resort, for a
  // mobile browser that is in neither list.
  function isMobileLike() {
    var ua = navigator.userAgent || '';
    if (isIOS()) return true;
    if (/Android|Mobile|Tablet|Silk|KAIOS|Opera Mini|IEMobile/i.test(ua)) return true;
    var uad = navigator.userAgentData;
    if (uad && uad.mobile === true) return true;
    try {
      return window.matchMedia('(pointer: coarse)').matches && (navigator.maxTouchPoints || 0) > 0;
    } catch (e) {}
    return false;
  }

  // The page's OWN side gutter, so the image gets the margins the visitor
  // actually sees instead of an invented border: .ils-ds__inner is this page's
  // container and its horizontal padding IS the gutter (18px mobile, 24px from
  // 750px up). Clamped so a future CSS change can produce neither a hairline nor
  // a huge empty frame.
  function gutterFor(block) {
    var inner = null;
    try { inner = block.closest('.ils-ds__inner'); } catch (e) {}
    if (!inner) inner = block.parentNode;
    var pad = 0;
    if (inner && inner.nodeType === 1) {
      var cs = window.getComputedStyle(inner);
      pad = Math.max(parseFloat(cs.paddingLeft) || 0, parseFloat(cs.paddingRight) || 0);
    }
    if (!(pad > 0)) pad = 18;
    return Math.max(12, Math.min(48, Math.round(pad)));
  }

  // The colour that is actually painted behind the card. The section wrapper is
  // transparent on this theme (measured: rgba(0,0,0,0)), so walk up until
  // something paints rather than trusting one ancestor.
  function pageBackground(el) {
    for (var n = el; n && n.nodeType === 1; n = n.parentNode) {
      var c = window.getComputedStyle(n).backgroundColor;
      if (c && c !== 'transparent' && !/rgba\(\s*0,\s*0,\s*0,\s*0\s*\)/.test(c)) return c;
    }
    return '#ffffff';
  }

  // Re-draw the capture centred on a canvas that is `pad` DEVICE pixels bigger
  // on every side, filled with the page background. Bitmap-only, so it cannot
  // affect the capture it is handed.
  function withGutter(canvas, pad, bg) {
    if (!canvas || !canvas.width || !(pad > 0)) return canvas;
    var out = document.createElement('canvas');
    out.width = canvas.width + pad * 2;
    out.height = canvas.height + pad * 2;
    var cx = out.getContext('2d');
    if (!cx) return canvas;
    cx.fillStyle = bg;
    cx.fillRect(0, 0, out.width, out.height);
    cx.drawImage(canvas, pad, pad);
    return out;
  }

  function download(blob, filename) {
    var url = URL.createObjectURL(blob);
    var a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.rel = 'noopener';
    a.style.cssText = 'position:fixed;left:-9999px;top:0;';
    document.body.appendChild(a);
    a.click();
    window.setTimeout(function () {
      if (a.parentNode) a.parentNode.removeChild(a);
      URL.revokeObjectURL(url);
    }, 4000);
  }

  function save(blob, filename, root) {
    function route(v) { try { if (root) root.setAttribute('data-shot-route', v); } catch (e) {} }
    var file = null;
    try { file = new File([blob], filename, { type: 'image/png' }); } catch (e) {}
    var canShare = !!(file && navigator.share && navigator.canShare &&
      navigator.canShare({ files: [file] }));
    if (!canShare) { route('download-unsupported'); download(blob, filename); return Promise.resolve(); }
    if (!isMobileLike()) { route('download-desktop'); download(blob, filename); return Promise.resolve(); }
    route('share');
    return navigator.share({ files: [file] }).then(function () {
      route('shared');
    }, function (err) {
      // A user-cancelled share sheet already did what the user wanted; only a
      // real refusal (lost transient activation -> NotAllowedError, unsupported
      // payload) falls through to the download route, so one tap is never two
      // saves.
      if (err && (err.name === 'AbortError' || err.name === 'CanceledError')) { route('share-cancelled'); return; }
      route('share-failed-download');
      download(blob, filename);
    });
  }

  // TASK-5055 — no order number and no email: a shared file's name is shown to
  // every app in the share sheet and is copied into whichever one the user picks,
  // so it must carry nothing that identifies the customer or their order. The
  // time keeps two saves on the same day from colliding.
  function filenameFor() {
    var d = new Date();
    var pad = function (n) { return (n < 10 ? '0' : '') + n; };
    return 'ilovesnacks-order-' + d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' +
      pad(d.getDate()) + '-' + pad(d.getHours()) + pad(d.getMinutes()) + '.png';
  }

  function init(root) {
    if (!root || root.__ilsDsShotReady) return;
    var heading = root.querySelector('[data-ds-shot]');
    var block = root.querySelector('#ils-ds-check');
    var results = root.querySelector('[data-ds-results]');
    if (!heading || !block || !results) return;
    root.__ilsDsShotReady = true;

    var busy = false;

    function capturable() {
      return !results.hidden && !!results.querySelector('.ils-ds__item');
    }
    function sync() {
      heading.classList.toggle('is-shootable', capturable());
    }

    // The checker rewrites results.innerHTML and flips `hidden`; both are
    // observed so the affordance can never be left on after a second, failing
    // lookup clears the cards.
    try {
      new MutationObserver(sync).observe(results, {
        childList: true, subtree: true, attributes: true, attributeFilter: ['hidden'],
      });
    } catch (e) {}
    sync();

    heading.addEventListener('click', function () {
      if (busy || !capturable()) return;   // requirement 6: inert, silently
      busy = true;
      heading.classList.add('is-shooting');

      var rect = block.getBoundingClientRect();
      var want = { w: rect.width, h: rect.height };
      // The iOS canvas limits apply to the FINAL image, so the gutter has to sit
      // inside the budget. The clamp is monotonic, so including it can only ever
      // lower the scale, never raise it.
      var gutter = gutterFor(block);
      var w = Math.max(1, rect.width + gutter * 2);
      var h = Math.max(1, rect.height + gutter * 2);
      var scale = Math.min(window.devicePixelRatio || 1, 2);
      scale = Math.min(scale, MAX_SIDE / w, MAX_SIDE / h, Math.sqrt(MAX_AREA / (w * h)));
      scale = Math.max(1, scale);

      var bg = pageBackground(block);

      var imgs = [].slice.call(block.querySelectorAll('img'));
      var inlined = {};

      function finish() {
        busy = false;
        heading.classList.remove('is-shooting');
      }

      var fontsReady = (document.fonts && document.fonts.ready)
        ? document.fonts.ready : Promise.resolve();

      fontsReady
        .then(function () {
          return Promise.all(imgs.map(function (img, i) {
            return inlineImage(img, scale).then(
              function (url) { inlined[i] = url; },
              function () { /* leave the live src; useCORS below covers it */ }
            );
          }));
        })
        .then(function () { return loadLib(root.getAttribute(LIB_ATTR)); })
        .then(function (html2canvas) {
          // Did dropping the skipped subtrees move or resize the range inside the
          // clone? If so the crop rectangle — which comes from the REAL document
          // — no longer lines up and the pass has to be redone unfiltered.
          // The invariant is the range's own SIZE, deliberately not its position.
          // html2canvas crops with parseBounds(clonedElement), i.e. it measures
          // the CLONE, so a document-wide offset cannot misalign the image — and
          // there always is one: the cloner swaps every custom element for an
          // <html2canvascustomelement>, which the theme's tag-name CSS no longer
          // matches, so the header collapses. Measured here: the clone sits 58px
          // higher with the skip list than without it, while the range box is
          // 839.9px tall and 354px wide either way and the two canvases come out
          // pixel-identical. What WOULD be fatal is a reflow of the range itself
          // (different wrapping, a collapsed card), and that always shows up as a
          // different width or height.
          function cloneIsFaithful(doc) {
            try {
              var cb = doc.querySelector('#ils-ds-check');
              if (!cb) return false;
              var r = cb.getBoundingClientRect();
              return Math.abs(r.height - want.h) <= 1 && Math.abs(r.width - want.w) <= 1;
            } catch (e) { return false; }
          }

          // Poll rather than measure once: the clone is laid out by copies of the
          // page's own <link>/<style> elements inside a brand-new iframe, so an
          // early read can catch it half-styled. This doubles as the readiness
          // gate the library does not have.
          function settle(doc, tries) {
            if (cloneIsFaithful(doc)) return Promise.resolve(true);
            if (tries <= 0) return Promise.resolve(false);
            return new Promise(function (r) { window.setTimeout(r, 60); })
              .then(function () { return settle(doc, tries - 1); });
          }

          var faithful = true;

          function pass(skip) {
            faithful = true;
            return html2canvas(block, {
              backgroundColor: bg,
              scale: scale,
              useCORS: true,
              allowTaint: false,
              logging: false,
              imageTimeout: 20000,
              removeContainer: true,
              ignoreElements: skip ? function (node) {
                return !!SKIP_TAGS[node.tagName] && !node.contains(block) && !block.contains(node);
              } : undefined,
              onclone: function (doc) {
                // The capture must show the page at rest: drop the pulse the real
                // heading is wearing right now and the pointer affordance class.
                var h2 = doc.querySelector('[data-ds-shot]');
                if (h2) h2.classList.remove('is-shooting', 'is-shootable');
                var b = doc.querySelector('#ils-ds-check');
                if (!b) return;
                [].slice.call(b.querySelectorAll('img')).forEach(function (img, i) {
                  if (!inlined[i]) return;
                  img.src = inlined[i];
                  img.removeAttribute('srcset');
                  img.removeAttribute('sizes');
                  img.removeAttribute('loading');
                });
                return settle(doc, 30).then(function (ok) {
                  // Only the filtered pass can be WRONG because of a shift; for
                  // the unfiltered pass the same wait is pure robustness.
                  if (skip) faithful = ok;
                });
              },
            });
          }

          // data-shot-noskip forces the unfiltered pass; it exists so the
          // fallback branch is exercised by the acceptance run instead of being
          // code that never executes until it is needed.
          if (root.hasAttribute('data-shot-noskip')) return pass(false);
          return pass(true).then(function (cv) {
            if (faithful) return cv;
            root.setAttribute('data-shot-fallback', '1');
            return pass(false);
          });
        })
        .then(function (canvas) {
          // TASK-5055 — the page's own side/top/bottom gutter, added to the
          // BITMAP after the capture so the layout that was captured stays
          // exactly the layout the visitor is looking at.
          var out = withGutter(canvas, Math.round(gutter * scale), bg);
          root.setAttribute('data-shot-gutter', String(Math.round(gutter * scale)));
          if (out) root.setAttribute('data-shot-dims', out.width + 'x' + out.height);
          return out;
        })
        .then(function (canvas) {
          return new Promise(function (resolve) {
            if (canvas && canvas.toBlob) canvas.toBlob(function (blob) { resolve(blob); }, 'image/png');
            else resolve(null);
          });
        })
        .then(function (blob) {
          if (!blob) { finish(); return; }
          // Left on the section root purely so an automated run can assert a
          // real PNG was produced without touching the user's download.
          root.setAttribute('data-shot-size', String(blob.size));
          return save(blob, filenameFor(), root).then(finish, finish);
        })
        .catch(function () { finish(); });
    });
  }

  function boot() {
    var roots = document.querySelectorAll('[data-ils-ds]');
    for (var i = 0; i < roots.length; i++) init(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);
})();
